/**
 * EditSessionModal Component
 * Modal for editing individual session details including meeting link
 * MED-170: Added to support session-level meeting link configuration
 * MED-71: Added confirmation dialog for published sessions with reschedule message
 */

import { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  TextInput,
  Select,
  Text,
  Textarea,
  Alert,
  Switch,
  Badge,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Video, AlertTriangle, MapPin, Lock } from 'react-feather';
import { cohortsApi, type SessionUpdate } from '../../../services/api/cohorts';
import { isCheckinOneOnOneType, type Session } from '../../../types';
import { getOneOnOneTypeConfig } from '../../../config/oneOnOneTypeConfig';
import { DateTimeField } from '../../../components/common';
import { utcToTimezoneDate, timezoneDateToUtc, INTERNAL_TIMEZONE } from '../../../utils/timezone';
import { platformDefaultLink } from '../../../utils/meetingLink';
import {
  DEFAULT_START_TIME_ON_DATE_PICK,
  defaultSessionLengthMinutes,
  endFieldDefaults,
  endTimeError,
  nextEndTime,
  type SessionLengthModality,
} from '../../../utils/sessionEndTime';

interface EditSessionModalProps {
  opened: boolean;
  onClose: () => void;
  /**
   * MED-1350: getById adds session_coach_meeting_link, the profile link of the
   * session's own coach (sessions.coach_id). A 1:1 prefills from it.
   */
  session: (Session & { session_coach_meeting_link?: string | null }) | null;
  cohort: {
    id: string;
    default_meeting_link?: string | null;
    /** MED-1360: the default link prefills only when its host matches this platform. */
    default_meeting_platform?: string | null;
    /**
     * MED-1350: optional. Gives the session length when the row has none: the
     * 1:1 length for a 1:1, the group_coaching length for a group session.
     */
    modalities?: SessionLengthModality[];
  };
  onUpdate: (session: Session) => void;
  /** E18-3: When true, hides Status field and auto-derives status from dates */
  isOneOnOne?: boolean;
  /** MED-1350: participant name for a check-in title ("Intro check-in · Name") */
  participantName?: string;
  /**
   * MED-1350: whether this viewer may clear the date. The server lets staff
   * clear any 1:1 and a coach only a session they coach. Defaults to true.
   */
  canClearDate?: boolean;
}

/** MED-1350: a dated 1:1 in these states keeps its date. */
const DATE_LOCKED_STATUSES = new Set(['completed', 'cancelled', 'in_progress']);

// Format display Date to UTC ISO string for API
// The date from the form represents ET wall-clock time, so convert back to UTC
function toUTCISOString(date: Date | null): string | undefined {
  if (!date) return undefined;
  const utcDate = timezoneDateToUtc(date, INTERNAL_TIMEZONE);
  return utcDate ? utcDate.toISOString() : undefined;
}

export function EditSessionModal({
  opened,
  onClose,
  session,
  cohort,
  onUpdate,
  isOneOnOne = false,
  participantName,
  canClearDate = true,
}: EditSessionModalProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  // MED-1350: inline confirm for "Clear date".
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [rescheduleMessage, setRescheduleMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // MED-866 / E1.12: AI Listening saves immediately via its own endpoint
  // (independent of the Review Changes flow, which re-syncs the calendar and
  // notifies attendees — an AI Listening change must not do either).
  const [aiListeningEnabled, setAiListeningEnabled] = useState(false);
  const [aiListeningSaving, setAiListeningSaving] = useState(false);

  const form = useForm({
    initialValues: {
      title: '',
      scheduled_start: null as Date | null,
      scheduled_end: null as Date | null,
      // MED-1007 (E4.0g): a `missed` session can be opened here to reschedule
      // it, so the form must be able to hold that status for display.
      status: 'scheduled' as 'scheduled' | 'completed' | 'cancelled' | 'missed',
      zoom_link: '',
      // MED-780: Free-text physical location for the calendar invite.
      location: '',
    },
  });

  // Check if session is published (finalized with invites sent)
  const isPublished = Boolean(session?.finalized_at && session?.invites_sent_at);

  // MED-866 / E1.12 — AI Listening field gating + lock.
  // Show the field only when the cohort's group coaching modality has AI
  // features enabled (server-denormalized onto the session) and this is a group
  // session, not a 1:1 (AC4). The field is absent entirely otherwise (AC3).
  const showAiListening =
    !isOneOnOne &&
    session?.ai_features_enabled === true &&
    session?.session_type === 'group_session';
  // The 15-minute lock window (scheduled_start - 15 min). Compared in epoch ms
  // (UTC) so the wall-clock timezone is irrelevant. Once locked — or the session
  // is in progress/completed — the setting is frozen (AC5/AC10). The server is
  // authoritative; this only governs the read-only display.
  const AI_LISTENING_LOCK_MS = 15 * 60 * 1000;
  const aiStartMs = session?.scheduled_start ? new Date(session.scheduled_start).getTime() : null;
  // The frontend SessionStatus union is narrower than the DB enum (it omits the
  // runtime-only 'in_progress' state), so compare as a string. The time-based
  // check below independently covers any started session regardless of status.
  const aiSessionStatus = session?.status as string | undefined;
  const aiListeningLocked =
    aiSessionStatus === 'in_progress' ||
    aiSessionStatus === 'completed' ||
    (aiStartMs !== null && Date.now() >= aiStartMs - AI_LISTENING_LOCK_MS);

  // MED-1360: a cohort default from a different platform is not prefilled.
  const cohortDefaultLink = platformDefaultLink(
    cohort.default_meeting_link,
    cohort.default_meeting_platform,
  );

  // Initialize form when modal opens with session data
  useEffect(() => {
    if (opened && session) {
      // A link saved on the session always wins.
      // MED-1350: a 1:1 then takes the link from the profile of the session's
      // own coach, never the participant's 1:1 coach or the viewer, then the
      // cohort default. It stays empty only when none exists.
      // For group: the session link, then the cohort default.
      const effectiveZoomLink = isOneOnOne
        ? (session.zoom_link || session.session_coach_meeting_link || cohortDefaultLink)
        : (session.zoom_link || cohortDefaultLink);

      const initialStart = utcToTimezoneDate(
        session.scheduled_start ? new Date(session.scheduled_start) : null,
        INTERNAL_TIMEZONE,
      );
      const savedEnd = utcToTimezoneDate(
        session.scheduled_end ? new Date(session.scheduled_end) : null,
        INTERNAL_TIMEZONE,
      );
      // MED-1350: a dated row with no saved end opens with its end filled from
      // the usual length, so a change to the link alone can still save.
      const initialEnd =
        initialStart && !savedEnd
          ? nextEndTime({
              previousStart: null,
              previousEnd: null,
              nextStart: initialStart,
              defaultLengthMinutes: defaultSessionLengthMinutes(session, {
                cohortModalities: cohort.modalities,
              }),
            })
          : savedEnd;

      form.setValues({
        title: session.title || '',
        scheduled_start: initialStart,
        scheduled_end: initialEnd,
        status: session.status,
        zoom_link: effectiveZoomLink,
        // MED-780: Hydrate from saved session location, or empty if unset.
        location: session.location ?? '',
      });

      // MED-866: hydrate the AI Listening toggle from the session's current value.
      setAiListeningEnabled(session.ai_listening_enabled ?? false);

      // Reset confirmation state when modal opens
      setShowConfirmation(false);
      setRescheduleMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, session, cohortDefaultLink]);

  const handleSubmitAttempt = () => {
    if (!session) return;

    // MED-1350: never save a dated row without a start. An emptied start on a
    // dated 1:1 goes to the Clear date confirm; otherwise ask for a start.
    if (!form.values.scheduled_start && (hasSavedDate || isOneOnOne)) {
      if (showClearDate) {
        setShowClearConfirm(true);
      } else {
        form.setFieldError('scheduled_start', 'Enter a start time.');
      }
      return;
    }
    // MED-1350: a start of 12:00 AM saves as it is. A date pick gives 9:00 AM,
    // so midnight is a time the user chose.
    if (endError) return;

    // Validate URL if provided
    if (form.values.zoom_link.trim()) {
      try {
        new URL(form.values.zoom_link.trim());
      } catch {
        form.setFieldError('zoom_link', 'Please enter a valid URL');
        return;
      }
    }

    // If session is published, show confirmation dialog first
    if (isPublished) {
      setShowConfirmation(true);
    } else {
      // Not published, submit directly
      handleConfirmedSubmit();
    }
  };

  const handleConfirmedSubmit = async () => {
    if (!session) return;

    setIsSubmitting(true);
    try {
      // For 1:1 sessions, auto-derive status: 'scheduled' if dates set, 'unscheduled' otherwise
      // MED-1007 (E4.0g · AC5): rescheduling a `missed` group session returns it
      // to the normal lifecycle. The CM reschedules via this date edit, so when
      // the status is still 'missed' on save, derive 'scheduled' — otherwise the
      // modal would re-send 'missed' and defeat the server-side return-to-scheduled.
      const derivedStatus = isOneOnOne
        ? (form.values.scheduled_start ? 'scheduled' : 'unscheduled')
        : form.values.status === 'missed'
          ? 'scheduled'
          : form.values.status;

      const updateData: SessionUpdate = {
        title: form.values.title,
        scheduled_start: toUTCISOString(form.values.scheduled_start),
        scheduled_end: toUTCISOString(form.values.scheduled_end),
        status: derivedStatus as 'scheduled' | 'completed' | 'cancelled',
        zoom_link: form.values.zoom_link.trim() || null,
        // MED-780: Empty string is normalized to null on the backend.
        location: form.values.location.trim() || null,
      };

      // Include reschedule message if provided (only for published sessions)
      if (isPublished && rescheduleMessage.trim()) {
        updateData.reschedule_message = rescheduleMessage.trim();
      }

      const updated = await cohortsApi.updateSession(cohort.id, session.id, updateData);

      notifications.show({
        title: 'Success',
        message: isPublished
          ? 'Session updated. Calendar invites will be updated and attendees notified.'
          : 'Session updated successfully',
        color: 'green',
      });

      onUpdate(updated);
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update session',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // MED-866 / E1.12 — toggle AI Listening with optimistic update + revert on
  // error. Saves immediately via the dedicated CM endpoint (AC4); the server
  // enforces the CM/Admin guard (AC8) and the 15-minute lock (AC10).
  const handleAiListeningToggle = async (next: boolean) => {
    if (!session || aiListeningLocked || aiListeningSaving) return;

    const previous = aiListeningEnabled;
    setAiListeningEnabled(next); // optimistic
    setAiListeningSaving(true);
    try {
      const result = await cohortsApi.setSessionAiListening(cohort.id, session.id, next);
      setAiListeningEnabled(result.ai_listening_enabled);
      // Reflect the change in the parent's session list without closing the modal.
      onUpdate({ ...session, ai_listening_enabled: result.ai_listening_enabled });
      notifications.show({
        title: 'AI Listening updated',
        message: result.ai_listening_enabled
          ? 'Participants will be notified and listened to during this session.'
          : 'Participants will not be listened to during this session.',
        color: 'green',
      });
    } catch (error) {
      setAiListeningEnabled(previous); // revert
      notifications.show({
        title: 'Could not update AI Listening',
        message: error instanceof Error ? error.message : 'Please try again in a moment.',
        color: 'red',
      });
    } finally {
      setAiListeningSaving(false);
    }
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setShowClearConfirm(false);
    setRescheduleMessage('');
    onClose();
  };

  // MED-1350: "Clear date" returns a dated 1:1 or check-in to unscheduled.
  const isOneOnOneRow = session?.session_type === 'one_on_one_session';
  const hasSavedDate = Boolean(session?.scheduled_start);
  const showClearDate =
    isOneOnOne &&
    isOneOnOneRow &&
    hasSavedDate &&
    canClearDate &&
    !DATE_LOCKED_STATUSES.has(session?.status ?? '');
  // A Calendly booking moves only through Calendly (or Clear date, which
  // cancels it). Saving a new time here would leave the booking at the old time.
  const isCalendlyBooked = Boolean(
    isOneOnOneRow && hasSavedDate && session?.calendly_event_uri,
  );

  const handleClearDate = async () => {
    if (!session) return;
    setIsClearing(true);
    try {
      await cohortsApi.clearSessionDate(cohort.id, session.id);
      notifications.show({
        title: 'Date cleared',
        message: 'The session is unscheduled again.',
        color: 'green',
      });
      // SessionStatus has no 'unscheduled', though 1:1 rows use it. The callers
      // reload the list, so the cast only carries the value through.
      onUpdate({
        ...session,
        status: 'unscheduled' as unknown as Session['status'],
        scheduled_start: null,
        scheduled_end: null,
      });
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Could not clear the date',
        message: error instanceof Error ? error.message : 'Failed to clear the date',
        color: 'red',
      });
    } finally {
      setIsClearing(false);
    }
  };

  // MED-1159: Preserve the session's currently scheduled duration when the start
  // time changes, the way every standard calendar tool does. Without this,
  // moving the start (especially across days) leaves the end untouched and
  // silently produces a malformed event — e.g. a ~2-day span for a 60-minute
  // session. The duration is derived from the existing form start→end span
  // (AC2 — NOT the cohort's configured session length), so a session manually
  // adjusted to an off-standard length keeps that length when rescheduled.
  // The End Time field stays editable, so the CM can still override afterward
  // (AC3). Both values are ET wall-clock Dates with the same offset, so the
  // getTime() delta is the true span in ms regardless of timezone.
  //
  // MED-1350: the shared rule in utils/sessionEndTime.ts. A start with no
  // valid end fills the end from the session's usual length
  // (defaultSessionLengthMinutes). An end the user set keeps its length.
  const sessionLengthMinutes = session
    ? defaultSessionLengthMinutes(session, { cohortModalities: cohort.modalities })
    : null;
  const handleStartTimeChange = (newStart: Date | null) => {
    const previousStart = form.values.scheduled_start;
    const previousEnd = form.values.scheduled_end;
    form.setFieldValue('scheduled_start', newStart);
    form.setFieldValue(
      'scheduled_end',
      nextEndTime({
        previousStart,
        previousEnd,
        nextStart: newStart,
        defaultLengthMinutes: sessionLengthMinutes,
      }),
    );
  };

  // MED-1360: an empty End field opens on the start's month and shows a time.
  // A date-only pick gives the start plus the usual length. A typed end is
  // kept as typed; endError reports one at or before the start.
  const endDefaults = endFieldDefaults(form.values.scheduled_start, sessionLengthMinutes);
  const handleEndTimeChange = (value: Date | null) => {
    form.setFieldValue('scheduled_end', value);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setRescheduleMessage('');
  };

  if (!session) return null;

  // MED-1350: an end at or before the start, or a start with no end, blocks
  // Save. A Calendly booking cannot be edited here, so it never blocks.
  const endError = isCalendlyBooked
    ? undefined
    : endTimeError(form.values.scheduled_start, form.values.scheduled_end);

  // MED-1350: a check-in has no session number. Title it by its name.
  const modalTitle = isCheckinOneOnOneType(session.one_on_one_type)
    ? [
        getOneOnOneTypeConfig(session.one_on_one_type).displayLabel,
        participantName,
      ]
        .filter(Boolean)
        .join(' · ')
    : isOneOnOne && participantName
      ? // MED-1350: a 1:1 names its participant, like a check-in title.
        [session.session_number != null ? `Session ${session.session_number}` : 'Session', participantName].join(' · ')
      : session.session_number != null
        ? `Edit Session ${session.session_number}`
        : 'Edit Session';

  // Render confirmation dialog for published sessions
  if (showConfirmation) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        title={
          <Group gap="xs">
            <AlertTriangle size={20} color="var(--mantine-color-orange-6)" />
            <Text fw={600} size="lg">
              Update Published Session?
            </Text>
          </Group>
        }
        size="md"
      >
        <Stack gap="md">
          <Alert color="orange" variant="light">
            <Text size="sm">
              This session has already been published and calendar invites have been sent to
              attendees. Saving these changes will:
            </Text>
            <Stack gap={4} mt="xs">
              <Text size="sm">• Update the calendar event for all attendees</Text>
              <Text size="sm">• Send update notifications to all invitees</Text>
            </Stack>
          </Alert>

          <Textarea
            label="Reschedule Message (Optional)"
            description="Add an optional message to include in the update notification"
            placeholder="e.g., Session rescheduled due to facilitator conflict..."
            value={rescheduleMessage}
            onChange={(e) => setRescheduleMessage(e.currentTarget.value)}
            minRows={3}
            maxRows={5}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleCancelConfirmation} disabled={isSubmitting}>
              Go Back
            </Button>
            <Button color="orange" onClick={handleConfirmedSubmit} loading={isSubmitting}>
              Confirm & Update
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  // Render main edit form
  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={600} size="lg">
          {modalTitle}
        </Text>
      }
      size="md"
    >
      <Stack gap="md">
        {isPublished && (
          <Alert color="orange" variant="light" icon={<AlertTriangle size={16} />}>
            <Text size="sm">
              This session is published. Changes will update calendar invites and notify attendees.
            </Text>
          </Alert>
        )}

        <TextInput
          label="Title"
          placeholder="Session title"
          {...form.getInputProps('title')}
        />

        <Group grow align="flex-start">
          <DateTimeField
            label="Start Time"
            placeholder="Select start time"
            value={form.values.scheduled_start}
            onChange={handleStartTimeChange}
            error={form.errors.scheduled_start as string | undefined}
            defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
            disabled={isCalendlyBooked}
          />
          <DateTimeField
            label="End Time"
            placeholder="Select end time"
            value={form.values.scheduled_end}
            onChange={handleEndTimeChange}
            minDate={form.values.scheduled_start || undefined}
            defaultDate={endDefaults.defaultDate}
            defaultTimeOnDatePick={endDefaults.defaultTimeOnDatePick}
            valueOnFirstDatePick={endDefaults.endOnDatePick}
            error={(form.errors.scheduled_end as string | undefined) ?? endError}
            disabled={isCalendlyBooked}
          />
        </Group>

        {isCalendlyBooked && (
          <Text size="xs" c="dimmed">
            Booked in Calendly. The participant reschedules there.
            {showClearDate ? ' Clear date cancels the booking.' : ''}
          </Text>
        )}

        {/* MED-1350: Clear date, with an inline confirm. */}
        {showClearDate &&
          (showClearConfirm ? (
            <Alert color="gray" variant="light">
              <Stack gap="xs">
                <Text size="sm">Remove the date from this session? It goes back to unscheduled.</Text>
                {isCalendlyBooked && (
                  <Text size="sm">The Calendly booking is canceled too.</Text>
                )}
                <Group gap="xs" justify="flex-end">
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => setShowClearConfirm(false)}
                    disabled={isClearing}
                  >
                    Keep date
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    onClick={() => void handleClearDate()}
                    loading={isClearing}
                  >
                    Clear date
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : (
            <Group justify="flex-start">
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => setShowClearConfirm(true)}
              >
                Clear date
              </Button>
            </Group>
          ))}

        {!isOneOnOne && (
          <Select
            label="Status"
            data={[
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
              // MED-1007 (E4.0g): 'Missed' is shown only when the session is
              // already missed (it is set by the system, never chosen here) so
              // the current status renders. Saving with a date reschedules it.
              ...(session.status === 'missed'
                ? [{ value: 'missed', label: 'Missed' }]
                : []),
            ]}
            {...form.getInputProps('status')}
          />
        )}

        {/* MED-866 / E1.12 — CM AI Listening pre-lock control. Present only when
            the cohort's group coaching modality has AI features enabled. */}
        {showAiListening && (
          <Stack gap={4}>
            <Group justify="space-between" align="center" wrap="nowrap">
              <Text fw={500} size="sm">
                AI Listening
              </Text>
              {aiListeningLocked ? (
                <Badge
                  color="gray"
                  variant="light"
                  leftSection={<Lock size={10} />}
                  styles={{ root: { textTransform: 'none', fontWeight: 500 } }}
                >
                  Locked
                </Badge>
              ) : (
                <Switch
                  size="sm"
                  checked={aiListeningEnabled}
                  disabled={aiListeningSaving}
                  onChange={(e) => void handleAiListeningToggle(e.currentTarget.checked)}
                  aria-label="Toggle AI Listening for this session"
                />
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {aiListeningEnabled
                ? 'Participants will be notified and listened to during the session.'
                : 'Participants will not be listened to during the session.'}
            </Text>
            {aiListeningLocked && (
              <Text size="xs" c="dimmed">
                Locked 15 minutes before the session starts — this can no longer be changed.
              </Text>
            )}
          </Stack>
        )}

        <TextInput
          label="Meeting Link"
          placeholder="Join Meeting | Zoom ..."
          description={
            session.zoom_link
              ? 'Session-specific meeting link'
              : isOneOnOne && session.session_coach_meeting_link
                ? "Pre-filled from the session coach's profile. Edit to override."
                : cohortDefaultLink
                  ? 'Pre-filled from cohort default. Edit to override.'
                  : cohort.default_meeting_link
                    ? "The cohort default link is for a different platform than the cohort's."
                    : 'No default meeting link set for this cohort'
          }
          leftSection={<Video size={16} />}
          {...form.getInputProps('zoom_link')}
        />

        {/* MED-780: Free-text physical location for the calendar invite. */}
        <TextInput
          label="Location"
          placeholder="e.g., Room 204, 123 Main St"
          description="Optional — appears as the location on calendar invites."
          leftSection={<MapPin size={16} />}
          maxLength={1000}
          {...form.getInputProps('location')}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmitAttempt} disabled={!!endError}>
            {isPublished ? 'Review Changes' : 'Save Changes'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditSessionModal;
